import React, { useState, useEffect, useRef } from 'react';
import { Button, FormControl, ControlLabel, FormGroup, Alert, Modal } from 'ui';
import API from 'apis/API';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '../../hooks/users/queries';

const UpdateEmailModal = ({ show, onHide }) => {
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [loading, setLoading] = useState(false);

  const emailInputRef = useRef(null);
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();
  const mfaEnabled = currentUser?.mfa_enabled;

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      setStep('form');
      setNewEmail('');
      setConfirmEmail('');
      setPassword('');
      setMfaCode('');
      setError('');
      setEmailError('');
      setPasswordError('');
      setMfaError('');
      setLoading(false);
    }
  }, [show]);

  // Focus email input when modal opens
  useEffect(() => {
    if (show && step === 'form' && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [show, step]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setError('');
    setEmailError('');
    setPasswordError('');
    setMfaError('');

    // Client-side validation
    let hasError = false;

    if (!newEmail) {
      setEmailError('Please enter a new email address');
      hasError = true;
    } else if (newEmail !== confirmEmail) {
      setEmailError('Email addresses do not match');
      hasError = true;
    } else if (currentUser?.email && newEmail.trim().toLowerCase() === currentUser.email.trim().toLowerCase()) {
      setEmailError('New email must be different from your current email');
      hasError = true;
    }

    if (!password) {
      setPasswordError('Please enter your password');
      hasError = true;
    }

    if (mfaEnabled && !mfaCode) {
      setMfaError('Please enter the verification code');
      hasError = true;
    }

    if (hasError) return;

    setLoading(true);
    try {
      const params = {
        email: newEmail,
        current_password: password,
      };
      if (mfaCode) params.mfa_code = mfaCode;

      await API.put('/api/auth', params);
      queryClient.invalidateQueries({
        queryKey: ['users', 'current']
      });
      setStep('success');
    } catch (err) {
      const responseData = err.response?.data;
      const errors = responseData?.errors;
      const errorMsg = Array.isArray(errors) ? errors[0] : (errors?.full_messages?.[0] || 'Something went wrong');

      if (responseData?.mfa_required) {
        setMfaError(errorMsg);
      } else if (errorMsg.toLowerCase().includes('password')) {
        setPasswordError(errorMsg);
      } else if (errorMsg.toLowerCase().includes('email')) {
        setEmailError(errorMsg);
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onHide();
  };

  const renderForm = () => (
    <form onSubmit={handleSubmit}>
      <Modal.Body>
        {error && (
          <Alert variant="danger">
            <p>{error}</p>
          </Alert>
        )}

        <FormGroup controlId="newEmail">
          <ControlLabel>New Email</ControlLabel>
          {emailError && (
            <Alert variant="warning" style={{ marginBottom: '5px', paddingTop: '8px', paddingBottom: '8px' }}>{emailError}</Alert>
          )}
          <FormControl
            type="email"
            placeholder="Enter new email address"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            autoComplete="email"
            ref={ref => { emailInputRef.current = ref; }}
          />
        </FormGroup>

        <FormGroup controlId="confirmEmail">
          <ControlLabel>Confirm New Email</ControlLabel>
          <FormControl
            type="email"
            placeholder="Confirm new email address"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            autoComplete="email"
          />
        </FormGroup>

        <FormGroup controlId="password">
          <ControlLabel>Password</ControlLabel>
          {passwordError && (
            <Alert variant="warning" style={{ marginBottom: '5px', paddingTop: '8px', paddingBottom: '8px' }}>{passwordError}</Alert>
          )}
          <FormControl
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        </FormGroup>

        {mfaEnabled && (
          <FormGroup controlId="mfaCode">
            <ControlLabel>Verification Code</ControlLabel>
            {mfaError && (
              <Alert variant="warning" style={{ marginBottom: '5px', paddingTop: '8px', paddingBottom: '8px' }}>{mfaError}</Alert>
            )}
            <FormControl
              type="text"
              placeholder="Enter 6 digit verification code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              maxLength={6}
              autoComplete="one-time-code"
            />
          </FormGroup>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="blue" type="submit" disabled={loading}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </form>
  );

  const renderSuccess = () => (
    <div>
      <Modal.Body>
        <Alert variant="success">
          <p>Check your inbox at <strong>{newEmail}</strong> to confirm your new email address.</p>
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="blue" onClick={handleClose}>Done</Button>
      </Modal.Footer>
    </div>
  );

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Change Email</Modal.Title>
      </Modal.Header>
      {step === 'success' ? renderSuccess() : renderForm()}
    </Modal>
  );
};

export default UpdateEmailModal;
